import { Component, ViewChild } from "@angular/core";
import { IBusinessDevelopmentAccountModel, IDropdownModel, DropdownModel, IGazingleRefTypeModel } from "@models";
import { SleepService, BusinessDevelopmentService, LookupService, GlobalsService, HttpService } from "@services";
import { ContactEditComponent } from "@components/contacts";
import { SlickDialogComponent } from "slick-components";
import { BusinessDevelopmentAccountTypesDialogComponent } from "../business-development-account-view/business-development-account-types-dialog/business-development-account-types-dialog.component";

@Component({
    selector: "business-development-account-edit-dialog",
    templateUrl: "business-development-account-edit-dialog.component.html",
    standalone: false
})

export class BusinessDevelopmentAccountEditDialogComponent {
	@ViewChild("dialogRef") dialogRef: SlickDialogComponent;
	@ViewChild("contactEditRef") contactEditRef: ContactEditComponent;
	@ViewChild("businessDevelopmentAccountTypesDialog") businessDevelopmentAccountTypesDialog: BusinessDevelopmentAccountTypesDialogComponent;
	businessDevelopmentAccount: IBusinessDevelopmentAccountModel;
	marketingEmployees: IDropdownModel[];
	accountTypes: IDropdownModel[];

	// 911 Restoration franchises map each referral account to one of the 911 referral types.
	isGazingleCompany: boolean = false;
	gazingleRefTypes: IDropdownModel[] = [];

	spinnerStatus: string;
	isSubmitted: boolean = false;

	resolve: any;

	constructor(private businessDevelopmentService: BusinessDevelopmentService, private lookupService: LookupService, private httpService: HttpService) {
	}

	async showDialog(businessDevelopmentAccount: IBusinessDevelopmentAccountModel): Promise<IBusinessDevelopmentAccountModel> {
		this.isSubmitted = false;
		this.spinnerStatus = "reset";
		this.businessDevelopmentAccount = businessDevelopmentAccount;
		this.accountTypes = await this.lookupService.getBusinessDevelopmentAccountTypesForDropdown();
		this.marketingEmployees = await this.lookupService.getMarketingEmployeesForDropdown(businessDevelopmentAccount.marketingEmployeeId);

		this.isGazingleCompany = (GlobalsService.companyInfo.parentCompanyId === 57);

		if (this.isGazingleCompany)
			await this.getGazingleRefTypes();

		return new Promise<IBusinessDevelopmentAccountModel>((resolve) => {
			this.resolve = resolve;

			this.dialogRef.showDialog();
		});

	}

	private async getGazingleRefTypes() {
		try {
			const refTypes: IGazingleRefTypeModel[] = await this.httpService.get("/gazingle/getGazingleRefTypes");
			this.gazingleRefTypes = refTypes.map(x => new DropdownModel(x.id, x.name));
		}
		catch (error) {
			// The 911 dictionary is a third party API - if it is down the rest of this dialog still works.
			console.error(error);
			this.gazingleRefTypes = [];
		}
	}

	async editAccountTypes() {
		await this.businessDevelopmentAccountTypesDialog.showDialog();

		this.accountTypes = await this.lookupService.getBusinessDevelopmentAccountTypesForDropdown();
	}

	async onAccountTypeChanged(accountType: IDropdownModel) {
		if (!accountType) {
			this.businessDevelopmentAccount.businessDevelopmentAccountTypeId = null;
			this.businessDevelopmentAccount.businessDevelopmentAccountTypeDescription = null;
		}
		else {
			this.businessDevelopmentAccount.businessDevelopmentAccountTypeId = accountType.id;
			this.businessDevelopmentAccount.businessDevelopmentAccountTypeDescription = accountType.text;
		}
	}

	// The name is stored alongside the id so nothing has to call the 911 dictionary to resolve it later.
	async onGazingleRefTypeChanged(refType: IDropdownModel) {
		if (!refType) {
			this.businessDevelopmentAccount.gazingleRefTypeId = null;
			this.businessDevelopmentAccount.gazingleRefTypeName = null;
		}
		else {
			this.businessDevelopmentAccount.gazingleRefTypeId = refType.id;
			this.businessDevelopmentAccount.gazingleRefTypeName = refType.text;
		}
	}

	async onMarketingEmployeeChanged(marketingEmployee: IDropdownModel) {
		if (!marketingEmployee) {
			this.businessDevelopmentAccount.marketingEmployeeId = null;
			this.businessDevelopmentAccount.marketingEmployeeFullName = null;
		}
		else {
			this.businessDevelopmentAccount.marketingEmployeeId = marketingEmployee.id;
			this.businessDevelopmentAccount.marketingEmployeeFullName = marketingEmployee.text;
		}
	}

	async onIsReferralChanged() {
		this.businessDevelopmentAccount.isReferral = !this.businessDevelopmentAccount.isReferral;
	}

	async onIsCarrierChanged() {
		this.businessDevelopmentAccount.isCarrier = !this.businessDevelopmentAccount.isCarrier;
	}
	
	validate(): boolean {
		this.isSubmitted = true;

		if (!this.contactEditRef.validate())
			return false;

		return true;
	}

	async onSave() {
		try {
			// Any validation needs to go here
			if (this.validate() === false) {
				this.spinnerStatus = "error";
				return;
			}

			this.spinnerStatus = "spin";

			await SleepService.sleep(500);

			if (this.businessDevelopmentAccount.businessDevelopmentAccountId === 0) 
				this.businessDevelopmentAccount = await this.businessDevelopmentService.addBusinessDevelopmentAccount(this.businessDevelopmentAccount);
			else
				this.businessDevelopmentAccount = await this.businessDevelopmentService.updateBusinessDevelopmentAccount(this.businessDevelopmentAccount);

            await this.lookupService.refreshBusinessDevelopmentAccounts();

			this.spinnerStatus = "ok";

			this.resolve(this.businessDevelopmentAccount);

			await SleepService.sleep(1000);

			this.dialogRef.hideDialog();

		}
		catch (error) {
			console.error(error);
			this.spinnerStatus = "error";
		}
	}

	onCancel() {
		this.resolve(null);
		this.dialogRef.hideDialog();
	}
}